import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { from } from 'rxjs';
import { Site, User } from 'src/app/classes/models';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { UserAccountRoles } from 'src/app/interfaces/user-account-roles';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { SiteService } from 'src/app/services/site.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-client-sites',
  templateUrl: './client-sites.component.html',
  styleUrls: ['./client-sites.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ClientSitesComponent {
  constructor(
    private Router: Router,
    private AuthService: AuthService,
    private ToastService: ToastService,
    private ModalService: NgbModal,
    private SiteService: SiteService
  ) {
    this.LoggedInUser = this.AuthService.getUser();

    const { UserAccountRole: { role } = {}, id: UserId } = this.LoggedInUser;

    if (role && !new Set(this.acceptedActionRoles).has(role)) {
      Object.assign(this.suppliedQueryData, {
        SiteUser: {
          options: { required: true },
          UserId
        }
      });
    }
  }

  @Input() showHeader: boolean = true;

  showDeactivated: boolean = false;
  reloadGrid: boolean = false;
  LoggedInUser: User;

  ColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['edit', 'view', 'add', 'remove']
  };
  TooltipMap: {
    [
      key in Extract<
        GridActionColumnActions,
        'edit' | 'view' | 'add' | 'remove'
      >
    ]: string;
  } = {
    edit: 'Edit Site',
    view: 'View Site',
    add: 'Activate Site',
    remove: 'Deactivate Site'
  };

  suppliedQueryData: GenericObject = {
    attributes: ['id', 'status'],
    status: 'Active'
  };

  acceptedActionRoles: UserAccountRoles[] = [
    'ADMIN_ADMIN',
    'ADMIN_CUSTOMER_SERVICE_TEAM',
    'ADMIN_PROJECT_MANAGEMENT_TEAM',

    'CLIENT_ADMIN'
  ];

  getCalculatedColumnActions: ({
    rowData
  }: {
    rowData: Site;
  }) => GridActionColumnActions[] = ({ rowData }: { rowData: Site }) => {
    const { status } = rowData;
    const ColumnActions: GridActionColumnActions[] = ['view'];

    const { UserAccountRole: { role } = {} } = this.LoggedInUser;

    if (role && new Set(this.acceptedActionRoles).has(role)) {
      if (status === 'Active') {
        const index = ColumnActions.findIndex((column) => column === 'add');

        if (index) {
          ColumnActions.slice(index, 1);
        }

        ColumnActions.push('remove');
      }

      if (status === 'Deactivated') {
        const index = ColumnActions.findIndex((column) => column === 'remove');

        if (index) {
          ColumnActions.slice(index, 1);
        }

        ColumnActions.push('add');
      }

      ColumnActions.unshift('edit');
    }

    return ColumnActions;
  };

  onShowDeactivatedSitesChange() {
    this.showDeactivated = !this.showDeactivated;

    if (this.showDeactivated) {
      Object.assign(this.suppliedQueryData, {
        status: { $in: ['Active', 'Deactivated'] }
      });
    }

    if (!this.showDeactivated) {
      Object.assign(this.suppliedQueryData, { status: 'Active' });
    }

    this.reloadGrid = true;
  }

  addSite() {
    this.Router.navigate([
      'client',
      this.AuthService.getUserCompanyId(),
      'site',
      'addSite'
    ]);
  }

  action({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: any;
  }) {
    if (!rowData) {
      throw this.ToastService.addErrorToast(
        'Error',
        'No row data found for Contract.',
        { returnError: true }
      );
    }

    const { id: SiteId } = rowData;

    if (!SiteId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'No Site ID found in row data.',
        { returnError: true }
      );
    }

    if (action === 'view') {
      this.Router.navigate([
        'client',
        this.AuthService.getUserCompanyId(),
        'site',
        SiteId,
        'siteDetails'
      ]);
    }

    if (action === 'edit') {
      this.Router.navigate([
        'client',
        this.AuthService.getUserCompanyId(),
        'site',
        SiteId,
        'editSite'
      ]);
    }

    if (action === 'remove') {
      const { componentInstance, result } = this.ModalService.open(
        ConfirmModalComponent,
        { centered: true, size: 'md' }
      );

      Object.assign(componentInstance, {
        displayText: 'Are you sure you want to deactivate this site?'
      });

      from(result).subscribe({
        next: (confirm) => {
          if (confirm) {
            this.SiteService.saveSite(
              Object.assign(rowData, { status: 'Deactivated' })
            ).subscribe({
              next: () => {
                this.ToastService.addSuccessToast(
                  'Success',
                  'Successfully deactivated site.'
                );
                this.reloadGrid = true;
              }
            });
          }
        }
      });
    }

    if (action === 'add') {
      this.SiteService.saveSite(
        Object.assign(rowData, { status: 'Active' })
      ).subscribe({
        next: () => {
          this.ToastService.addSuccessToast(
            'Success',
            'Successfully activated site.'
          );
          this.reloadGrid = true;
        }
      });
    }
  }
}
